import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Loader2, Send, Paperclip, Eye, X, Globe } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import RichTextEditor from '../ui/RichTextEditor';
import PoweredByFooter from '../shared/PoweredByFooter';
import BundleAttachmentPicker from './BundleAttachmentPicker';

const ConsignmentContractEmailDialog = ({ open, onClose, deliverableId, onSent, saleId, ownerId }) => {
  const [loading, setLoading]         = useState(true);
  const [sending, setSending]         = useState(false);
  const [form, setForm]               = useState({ to: '', cc: '', bcc: '', display_name: '', reply_to: '', subject: '', body: '', from_email: 'mailbox@mygreendoorapp.com' });
  const [attachment, setAttachment]   = useState(null);
  const [extraFileIds, setExtraFileIds] = useState([]);

  useEffect(() => {
    if (open && deliverableId) {
      setExtraFileIds([]);
      loadDraft();
    }
  }, [open, deliverableId]); // eslint-disable-line

  const loadDraft = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/consignment-contracts/email-draft/${deliverableId}`);
      const d = res.data;
      setForm({ to: d.to || '', cc: d.cc || '', bcc: d.bcc || '', display_name: d.display_name || '', reply_to: d.reply_to || '', subject: d.subject || '', body: d.body || '', from_email: d.from_email || 'mailbox@mygreendoorapp.com' });
      if (d.contract_file_id) {
        setAttachment({ file_id: d.contract_file_id, filename: d.contract_filename || 'contract.pdf' });
      }
    } catch { toast.error('Failed to load email draft'); }
    finally { setLoading(false); }
  };

  const openAttachment = () => {
    if (!attachment?.file_id) return;
    const token = localStorage.getItem('token');
    fetch(`${window.location.origin}/api/files/download/${attachment.file_id}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob()).then(blob => { window.open(URL.createObjectURL(blob), '_blank'); });
  };

  const handleSend = async () => {
    if (!form.to.trim()) { toast.error('Recipient email is required'); return; }
    setSending(true);
    try {
      await api.post(`/consignment-contracts/send-email/${deliverableId}`, {
        ...form,
        contract_file_id: attachment?.file_id,
        extra_file_ids: extraFileIds,
      });
      toast.success('Contract email sent');
      onSent?.(); onClose(true);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to send email'); }
    finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={() => onClose(false)}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Send Contract to Owner</DialogTitle></DialogHeader>
        {loading ? <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div> : (
          <div className="space-y-4">
            <div className="grid grid-cols-[80px_1fr] items-center gap-2"><Label className="font-semibold text-right">To</Label><Input value={form.to} onChange={e => setForm({ ...form, to: e.target.value })} placeholder="recipient@example.com" /></div>
            <div className="grid grid-cols-[80px_1fr] items-center gap-2"><Label className="text-right text-muted-foreground">CC</Label><Input value={form.cc} onChange={e => setForm({ ...form, cc: e.target.value })} /></div>
            <div className="border rounded-md p-3 space-y-2 bg-muted/20">
              <div className="grid grid-cols-[80px_1fr] items-center gap-2"><Label className="font-semibold text-right">From</Label><span className="text-sm text-muted-foreground">{form.from_email}</span></div>
              <div className="grid grid-cols-[80px_1fr] items-center gap-2 pl-4"><Label className="text-right text-sm text-muted-foreground">Display Name</Label><Input value={form.display_name} onChange={e => setForm({ ...form, display_name: e.target.value })} className="h-8" /></div>
              <div className="grid grid-cols-[80px_1fr] items-center gap-2 pl-4"><Label className="text-right text-sm text-muted-foreground">Reply To</Label><Input value={form.reply_to} onChange={e => setForm({ ...form, reply_to: e.target.value })} className="h-8" /></div>
            </div>
            <div className="grid grid-cols-[80px_1fr] items-center gap-2"><Label className="font-semibold text-right">RE:</Label><Input value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} /></div>
            <RichTextEditor value={form.body} onChange={v => setForm({ ...form, body: v })} />
            <PoweredByFooter />

            {/* Primary attachment */}
            <div className="border rounded-md p-3 bg-muted/20 space-y-2">
              <p className="text-xs font-semibold text-muted-foreground uppercase">Attachment</p>
              {attachment ? (
                <div className="flex items-center gap-3 p-2 border rounded bg-white">
                  <Paperclip className="w-4 h-4 text-muted-foreground shrink-0" />
                  <span className="text-sm truncate flex-1">{attachment.filename}</span>
                  <Button variant="ghost" size="sm" className="h-7 px-2" onClick={openAttachment} title="Preview"><Eye className="w-3.5 h-3.5" /></Button>
                  <Button variant="ghost" size="sm" className="h-7 px-2 text-red-500" onClick={() => setAttachment(null)} title="Remove"><X className="w-3.5 h-3.5" /></Button>
                </div>
              ) : (
                <p className="text-xs text-muted-foreground italic">No attachment</p>
              )}
            </div>

            {/* Bundle attachment picker (Components 2 & 3) */}
            {saleId && ownerId && (
              <BundleAttachmentPicker
                saleId={saleId}
                ownerId={ownerId}
                excludeFileIds={attachment?.file_id ? [attachment.file_id] : []}
                onChange={setExtraFileIds}
              />
            )}

            {/* Auto-publish note */}
            <div className="flex items-start gap-2 text-xs text-muted-foreground bg-blue-50 border border-blue-100 rounded-md px-3 py-2">
              <Globe className="w-3.5 h-3.5 shrink-0 mt-0.5 text-blue-500" />
              <span>Sending this email will automatically publish the contract to the owner portal.</span>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onClose(false)} disabled={sending}>Cancel</Button>
          <Button onClick={handleSend} disabled={sending || loading} className="bg-primary hover:bg-primary/90 text-white">
            {sending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</> : <><Send className="w-4 h-4 mr-2" /> Send Email</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ConsignmentContractEmailDialog;
